import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded'
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import ChatRoundedIcon from '@mui/icons-material/ChatRounded'
import moment from 'moment'
import { openCrispChat } from '../../helpers/useCrisp'
import {
  TICKET_STEPS,
  TYPE_CONFIGS,
  formatTicketDate,
  getPriorityConfig,
  getStatusConfig,
  getTypeKey,
} from './supportTicketMeta'

const BRAND = {
  dark: '#101827',
  navy: '#1A2230',
  slate: '#242F40',
  teal: '#239AA4',
  tealLight: '#46D7E1',
  ink: '#1A2230',
  body: '#334155',
  line: 'rgba(15,23,42,0.08)',
}

export default function SelectedFeedback({
  selectedFeedback,
  setSelectedFeedback,
}) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const open = Boolean(selectedFeedback)
  if (!open) return null

  const {
    name,
    type,
    date,
    description,
    status,
    priority,
    ticketRef,
    feedback,
  } = selectedFeedback

  const typeMeta = TYPE_CONFIGS[getTypeKey(type)]
  const statusMeta = getStatusConfig(status)
  const priorityMeta = getPriorityConfig(priority?.priority ?? '')
  const TypeIcon = typeMeta.icon
  const isClosed = statusMeta.step === TICKET_STEPS.length - 1
  const submitted = date ? moment(Number(date)) : null
  const close = () => setSelectedFeedback(null)

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 5,
          overflow: 'hidden',
          boxShadow: '0 30px 90px rgba(15,23,42,0.28)',
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header — tinted by ticket type */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          px: 3,
          pt: 3,
          pb: 2.5,
          color: 'white',
          flexShrink: 0,
          background: `
            radial-gradient(circle at 0% 0%, ${typeMeta.color}55, transparent 45%),
            radial-gradient(circle at 100% 120%, rgba(70,215,225,0.18), transparent 40%),
            linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.slate} 55%, ${BRAND.dark} 100%)
          `,
        }}
      >
        {/* Oversized watermark icon */}
        <TypeIcon
          aria-hidden
          sx={{
            position: 'absolute',
            right: -18,
            bottom: -30,
            fontSize: 150,
            color: 'rgba(255,255,255,0.05)',
            transform: 'rotate(-12deg)',
          }}
        />

        <IconButton
          onClick={close}
          aria-label="Close"
          size="small"
          sx={{
            position: 'absolute',
            top: 14,
            right: 14,
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.10)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ pr: 5, mb: 1.5 }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              pl: 0.75,
              pr: 1.25,
              py: 0.4,
              borderRadius: 99,
              bgcolor: 'rgba(255,255,255,0.10)',
              border: '1px solid rgba(255,255,255,0.16)',
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: typeMeta.color,
              }}
            >
              <TypeIcon sx={{ fontSize: 13, color: 'white' }} />
            </Box>
            <Typography sx={{ fontSize: 12, fontWeight: 900, color: '#fff' }}>
              {typeMeta.longLabel}
            </Typography>
          </Stack>

          {ticketRef && <TicketRef value={ticketRef} />}
        </Stack>

        <Typography
          variant="h5"
          fontWeight={950}
          sx={{
            position: 'relative',
            lineHeight: 1.2,
            letterSpacing: '-0.02em',
            wordBreak: 'break-word',
            color: '#fff'
          }}
        >
          {name || 'Untitled request'}
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1.5, position: 'relative' }}
        >
          <HeaderFact
            label="Submitted"
            value={
              submitted?.isValid()
                ? `${formatTicketDate(date)} · ${submitted.fromNow()}`
                : 'Unknown'
            }
          />
          {!isClosed && (
            <HeaderFact
              label="Priority"
              value={priorityMeta.label}
              dot={priorityMeta.color}
            />
          )}
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack spacing={3}>
          {statusMeta.step === null ? (
            <WaitingBanner />
          ) : (
            <ProgressTracker step={statusMeta.step} color={statusMeta.color} />
          )}

          {feedback && <FeedbackMessage text={feedback} closed={isClosed} />}

          <Box>
            <SectionLabel>Your report</SectionLabel>
            <Box
              sx={{
                position: 'relative',
                p: 2.25,
                pl: 3,
                pr: 5,
                borderRadius: 3,
                bgcolor: '#F8FAFC',
                border: `1px solid ${BRAND.line}`,
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 12,
                  bottom: 12,
                  width: 3,
                  borderRadius: 99,
                  bgcolor: typeMeta.color,
                  opacity: 0.6,
                },
              }}
            >
              <FormatQuoteRoundedIcon
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 10,
                  fontSize: 28,
                  color: 'rgba(15,23,42,0.08)',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-line',
                  lineHeight: 1.7,
                  color: description ? BRAND.body : 'text.secondary',
                  fontStyle: description ? 'normal' : 'italic',
                  wordBreak: 'break-word',
                }}
              >
                {description || 'No description was provided.'}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Footer */}
      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{
          px: 3,
          py: 2,
          flexShrink: 0,
          borderTop: `1px solid ${BRAND.line}`,
          bgcolor: '#FBFCFE',
        }}
      >
        <Button
          startIcon={<ChatRoundedIcon />}
          onClick={() => {
            close()
            openCrispChat()
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            color: BRAND.teal,
            borderRadius: 3,
          }}
        >
          Add more info via live chat
        </Button>
        <Button
          onClick={close}
          variant="contained"
          disableElevation
          sx={{
            borderRadius: 3,
            px: 3,
            textTransform: 'none',
            fontWeight: 900,
            backgroundColor: BRAND.teal,
            '&:hover': { backgroundColor: '#1D858E' },
          }}
        >
          Close
        </Button>
      </Stack>
    </Dialog>
  )
}

function TicketRef({ value }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {})
  }
  return (
    <Tooltip title={copied ? 'Copied' : 'Copy ticket ID'} arrow>
      <Box
        component="button"
        type="button"
        onClick={copy}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.1,
          py: 0.45,
          borderRadius: 99,
          cursor: 'pointer',
          font: 'inherit',
          fontFamily: 'monospace',
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '0.04em',
          color: 'rgba(255,255,255,0.85)',
          bgcolor: 'transparent',
          border: '1px dashed rgba(255,255,255,0.28)',
          transition: 'all 0.15s ease',
          '&:hover': {
            color: 'white',
            borderColor: 'rgba(255,255,255,0.55)',
            bgcolor: 'rgba(255,255,255,0.06)',
          },
        }}
      >
        {value}
        {copied ? (
          <CheckRoundedIcon sx={{ fontSize: 14, color: BRAND.tealLight }} />
        ) : (
          <ContentCopyRoundedIcon sx={{ fontSize: 12, opacity: 0.7 }} />
        )}
      </Box>
    </Tooltip>
  )
}

function HeaderFact({ label, value, dot }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'rgba(255,255,255,0.5)',
        }}
      >
        {label}
      </Typography>
      {dot && (
        <Box
          sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot }}
        />
      )}
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#fff'  }}>{value}</Typography>
    </Stack>
  )
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        mb: 1,
        fontSize: 11,
        fontWeight: 900,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'text.secondary',
      }}
    >
      {children}
    </Typography>
  )
}

function ProgressTracker({ step, color }) {
  const last = TICKET_STEPS.length - 1
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        mb={1.5}
      >
        <SectionLabel>Progress</SectionLabel>
        <Typography sx={{ fontSize: 13, fontWeight: 900, color }}>
          {TICKET_STEPS[step]}
        </Typography>
      </Stack>

      <Box sx={{ position: 'relative', px: 0.5 }}>
        {/* Track + fill */}
        <Box
          sx={{
            position: 'absolute',
            left: 12,
            right: 12,
            top: 8,
            height: 4,
            borderRadius: 99,
            bgcolor: 'rgba(15,23,42,0.07)',
          }}
        >
          <Box
            sx={{
              width: `${(step / last) * 100}%`,
              height: '100%',
              borderRadius: 99,
              background: `linear-gradient(90deg, ${BRAND.teal}, ${color})`,
              transition: 'width 0.4s ease',
            }}
          />
        </Box>

        <Stack direction="row" justifyContent="space-between">
          {TICKET_STEPS.map((label, index) => {
            const done = index < step
            const current = index === step
            return (
              <Stack
                key={label}
                alignItems="center"
                spacing={0.75}
                sx={{ position: 'relative', width: 20 }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: done ? BRAND.teal : current ? color : '#fff',
                    border: done || current ? 'none' : '2px solid #E2E8F0',
                    boxShadow: current ? `0 0 0 5px ${color}22` : 'none',
                  }}
                >
                  {done && (
                    <CheckRoundedIcon sx={{ fontSize: 13, color: 'white' }} />
                  )}
                  {current && (
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        bgcolor: 'white',
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    // Labels are wider than their dots; keep them centred
                    // under the dot without affecting the row spacing.
                    width: 72,
                    textAlign: 'center',
                    fontSize: 10.5,
                    lineHeight: 1.2,
                    fontWeight: current ? 900 : 700,
                    color: current ? BRAND.ink : done ? BRAND.body : '#94A3B8',
                    display: {
                      xs: current ? 'block' : 'none',
                      sm: 'block',
                    },
                    // Pin the end labels to their dot's outer edge so they
                    // don't overhang the dialog. Stack zeroes child margins,
                    // hence a transform: (72 - 20) / 2 = 26px.
                    ...(index === 0 && {
                      transform: 'translateX(26px)',
                      textAlign: 'left',
                    }),
                    ...(index === last && {
                      transform: 'translateX(-26px)',
                      textAlign: 'right',
                    }),
                  }}
                >
                  {label}
                </Typography>
              </Stack>
            )
          })}
        </Stack>
      </Box>
    </Box>
  )
}

function WaitingBanner() {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{
        p: 2,
        borderRadius: 3,
        bgcolor: 'rgba(220,38,38,0.06)',
        border: '1px solid rgba(220,38,38,0.22)',
      }}
    >
      <ReportProblemRoundedIcon sx={{ color: '#DC2626', mt: 0.1 }} />
      <Box>
        <Typography sx={{ fontWeight: 900, color: '#991B1B' }}>
          Waiting for you
        </Typography>
        <Typography variant="body2" sx={{ color: BRAND.body, lineHeight: 1.6 }}>
          We need a little more information before we can carry on. Check your
          email for a message from AmbuNet Support, or get in touch via live
          chat.
        </Typography>
      </Box>
    </Stack>
  )
}

function FeedbackMessage({ text, closed }) {
  return (
    <Box>
      <SectionLabel>{closed ? 'Resolution' : 'Update from support'}</SectionLabel>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: 'white',
            background: `linear-gradient(135deg, ${BRAND.tealLight}, ${BRAND.teal})`,
            boxShadow: '0 6px 16px rgba(35,154,164,0.35)',
          }}
        >
          <SupportAgentRoundedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: 2,
            borderRadius: 3,
            borderTopLeftRadius: 4,
            bgcolor: 'rgba(35,154,164,0.07)',
            border: '1px solid rgba(35,154,164,0.22)',
          }}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 900, color: BRAND.teal, mb: 0.5 }}
          >
            AmbuNet Support
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-line',
              lineHeight: 1.7,
              color: BRAND.body,
              wordBreak: 'break-word',
            }}
          >
            {text}
          </Typography>
        </Box>
      </Stack>
    </Box>
  )
}
